import { useState, useEffect, useRef } from 'react';
import { getWorkoutSessionReport } from '../../../../services/workoutSessionApi';

export const useSessionReport = (sessionId, status, preloadedReport = null) => {
  const [report, setReport] = useState(preloadedReport);
  const [isLoading, setIsLoading] = useState(!preloadedReport);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    // If we have preloaded data, skip fetching
    if (preloadedReport) {
      setReport(preloadedReport);
      setIsLoading(false);
      return;
    }

    const pollForReport = async (id) => {
      try {
        const reportData = await getWorkoutSessionReport(id);
        if (reportData === null || reportData.status === 'creating') {
          pollTimerRef.current = setTimeout(() => pollForReport(id), 5000);
        } else {
          setReport(reportData);
          setIsGenerating(false);
          setIsLoading(false);
        }
      } catch (err) {
        setError(err.message || "Failed to poll for workout session report.");
        setIsGenerating(false);
        setIsLoading(false);
      }
    };

    const fetchReport = async () => {
      if (!sessionId || status?.toLowerCase() !== 'completed') {
        setReport(null);
        setIsLoading(false);
        setIsGenerating(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setReport(null);

      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

      try {
        const reportData = await getWorkoutSessionReport(sessionId);
        if (reportData === null || reportData.status === 'creating') {
          setIsGenerating(true);
          setIsLoading(false);
          pollForReport(sessionId);
        } else {
          setReport(reportData);
          setIsLoading(false);
        }
      } catch (err) {
        setError(err.message || "Failed to load workout session report.");
        setIsLoading(false);
      }
    };

    fetchReport();

    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, [sessionId, status, preloadedReport]);

  return { report, isLoading, isGenerating, error };
};